<template>
    <div>
        <PageTitle :title="$t('perfil.titulo')" sectionClass="strech"/>
        <section class="tw:bg-gray-50">
            <div class="tw:max-w-7xl tw:mx-auto">
                <div class="tw:min-h-[calc(100vh-255px)] tw:lg:h-auto">
                    <div class="tw:relative tw:flex tw:w-full tw:flex-col tw:gap-6 tw:p-2 tw:pb-4 tw:md:gap-8 tw:md:p-4 tw:lg:flex-row">
                        <PerfilSidebar
                            v-if="!esMovil"
                            :tabs="tabs"
                            :activeTab="activeTab"
                            @change-tab="changeTab"
                            class="relative"
                        />
                        <div class="tw:flex-1 tw:min-w-0">
                            <div v-if="error" class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg">
                                <p class="tw:font-bold">{{ $t('perfil.error_cargar') }}</p>
                                <p class="tw:text-sm tw:text-black">{{ error || $t('perfil.error_desconocido') }}</p>
                            </div>
                            <KeepAlive>
                                <DatosPersonales
                                    v-if="activeTab === 'datos'"
                                    :userData="perfil || {}"
                                />
                                <CanteraList
                                    v-else-if="activeTab === 'cantera'"
                                    :deportistas="deportistas"
                                    :loading="canteraLoading"
                                    :error="canteraError"
                                    :is-empty="canteraIsEmpty"
                                    :meta="canteraMeta"
                                    :page="canteraPage"
                                    :per-page="canteraPerPage"
                                    :search="canteraSearch"
                                    :detalle-cache="canteraDetalleCache"
                                    :detalle-loading-ids="canteraDetalleLoadingIds"
                                    :detalle-error-cache="canteraDetalleErrorCache"
                                    :is-selected="canteraIsSelected"
                                    :selected-count="canteraSelectedCount"
                                    :total-seleccionado="canteraTotalSeleccionado"
                                    :pago-loading="canteraPagoLoading"
                                    :pago-error="canteraPagoError"
                                    :on-refresh="onCanteraPagoCompletado"
                                    :listas="canteraListas"
                                    :listas-loading="canteraListasLoading"
                                    :selected-lista-id="canteraSelectedListaId"
                                    @expand="loadCanteraDetalle"
                                    @fetch="fetchDeportistas"
                                    @plazo-toggled="onCanteraPlazoToggled"
                                    @pago-completado="onCanteraPagoCompletado"
                                    @update:search="val => canteraSearch = val"
                                    @update:lista-id="canteraOnListaFilterChange"
                                />
                                <PerfilAbonosView v-else-if="activeTab === 'abonos'" />
                                <PerfilBalanceView v-else-if="activeTab === 'monedero'" />
                                <template v-else-if="activeTab === 'pedidos' && !pedidoCambioDatos && !pedidoEnviarEntradas">
                                    <PedidosList
                                        v-if="!mostrandoInvitaciones"
                                        :pedidos="pedidos"
                                        :loading="pedidosLoading"
                                        :error="pedidosError"
                                        :meta="pedidosMeta"
                                        :page="pedidosPage"
                                        :per-page="pedidosPerPage"
                                        :search="pedidosSearch"
                                        :tipo="pedidosTipo"
                                        @fetch="fetchPedidos"
                                        @update:search="val => pedidosSearch = val"
                                        @update:tipo="val => pedidosTipo = val"
                                        @cambiar-datos="onCambiarDatos"
                                        @enviar-entradas="onEnviarEntradas"
                                        @toggle-invitaciones="onToggleInvitaciones"
                                        @edit-success="fetchPedidos(pedidosPage)"
                                    />
                                    <div v-else>
                                        <div class="tw:flex tw:items-center tw:justify-between tw:mb-6">
                                            <h2 class="tw:text-xl tw:font-bold tw:text-slate-900 tw:m-0">{{ $t('perfil.invitaciones') }}</h2>
                                            <Button variant="default" @click="onToggleInvitaciones">
                                                <i class="material-icons tw:text-sm">arrow_back</i>
                                                {{ $t('perfil.volver') }}
                                            </Button>
                                        </div>
                                        <InvitacionesList
                                            :invitaciones="invitaciones"
                                            :loading="invitacionesLoading"
                                            :error="invitacionesError"
                                            :meta="invitacionesMeta"
                                            :page="invitacionesPage"
                                            :per-page="invitacionesPerPage"
                                            @fetch="fetchInvitaciones"
                                        />
                                    </div>
                                </template>
                                <CambiarDatosView
                                    v-else-if="activeTab === 'pedidos' && pedidoCambioDatos"
                                    :key="pedidoCambioDatos.id"
                                    :pedido="pedidoCambioDatos"
                                    @volver="pedidoCambioDatos = null"
                                    @cambios-guardados="onCambiosGuardados"
                                />
                            </KeepAlive>

                            <EnviarEntradasView
                                v-if="activeTab === 'pedidos' && pedidoEnviarEntradas"
                                :pedido="pedidoEnviarEntradas"
                                @volver="pedidoEnviarEntradas = null"
                            />
                            <InformacionPagoV2
                                v-if="activeTab === 'info_pago'"
                                :tid="configuracion.hash_teatro"
                                :user-id="perfil?.id"
                            />
                            <PerfilParcelasView v-if="activeTab === 'parcelas'" />
                        </div>
                    </div>
                    <PerfilBottomNav
                        v-if="esMovil"
                        :tabs="tabs"
                        :activeTab="activeTab"
                        @change-tab="changeTab"
                    />
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
    import { ref, computed, provide, onMounted } from 'vue';
    import store from '@/store';
    import { useVersionMovil } from '@/composables/common/useVersionMovil';
    import { useRoute } from '@/composables/common/router/useRouter';
    import PageTitle from '@/components/frontend/shared/PageTitle';
    import { usePerfil } from '@/composables/front/perfil/usePerfil';
    import { usePerfilTabs } from '@/composables/front/perfil/usePerfilTabs';
    import { useCantera } from '@/composables/front/perfil/useCantera';
    import { usePedidos } from '@/composables/front/perfil/usePedidos';
    import { useInvitaciones } from '@/composables/front/perfil/useInvitaciones';
    import { PERFIL_EDITING_KEY } from '@/composables/front/perfil/keys';
    import PerfilSidebar from '../components/navigation/PerfilSidebar.vue';
    import PerfilBottomNav from '../components/navigation/PerfilBottomNav.vue';
    import DatosPersonales from '@/modules/front/perfil/components/DatosPersonales.vue';
    import PerfilBalanceView from '../components/balance/PerfilBalanceView.vue';
    import PerfilAbonosView from './PerfilAbonosView.vue';
    import CanteraList from '../components/cantera/List.vue';
    import InformacionPagoV2 from '../components/InformacionPagoV2.vue';
    import PedidosList from '../components/PedidosList.vue';
    import InvitacionesList from '../components/InvitacionesList.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import CambiarDatosView from '../views/CambiarDatosView.vue';
    import EnviarEntradasView from '../views/EnviarEntradasView.vue';
    import PerfilParcelasView from './PerfilParcelasView.vue';
    import { useToast } from '@/composables/common/useToast';
    import { t } from '@/utils/i18n-helper';

    const route = useRoute();
    const { esMovil } = useVersionMovil(1024);
    const perfilState = usePerfil();
    const configuracion = computed(() => store.getters.getConfig);

    // Estado para la vista de cambiar datos
    const pedidoCambioDatos = ref(null);
    const onCambiarDatos = pedido => {
        pedidoCambioDatos.value = pedido;
    };
    const onCambiosGuardados = async() => {
        await fetchPedidos(1);
        pedidoCambioDatos.value = null;
    };

    // Estado para la vista de enviar entradas
    const pedidoEnviarEntradas = ref(null);
    const onEnviarEntradas = pedido => {
        pedidoEnviarEntradas.value = pedido;
    };

    provide(PERFIL_EDITING_KEY, {
        isEditing:     perfilState.isEditing,
        localData:     perfilState.localData,
        loading:       perfilState.loading,
        errors:        perfilState.errors,
        startEditing:  perfilState.startEditing,
        cancelEditing: perfilState.cancelEditing,
        save:          perfilState.save,
    });

    const { error, perfil, cancelEditing } = perfilState;
    const { tabs, activeTab, changeTab: changeTabBase } = usePerfilTabs();

    const {
        pedidos,
        loading: pedidosLoading,
        error: pedidosError,
        meta: pedidosMeta,
        page: pedidosPage,
        perPage: pedidosPerPage,
        search: pedidosSearch,
        tipo: pedidosTipo,
        hasFetched: hasFetchedPedidos,
        fetchPedidos,
    } = usePedidos();

    const {
        invitaciones,
        loading: invitacionesLoading,
        error: invitacionesError,
        meta: invitacionesMeta,
        page: invitacionesPage,
        perPage: invitacionesPerPage,
        fetchInvitaciones,
    } = useInvitaciones();

    const mostrandoInvitaciones = ref(false);

    const onToggleInvitaciones = () => {
        mostrandoInvitaciones.value = !mostrandoInvitaciones.value;
        if (mostrandoInvitaciones.value) {
            fetchInvitaciones(1);
        }
    };

    const {
        deportistas,
        loading: canteraLoading,
        error: canteraError,
        search: canteraSearch,
        meta: canteraMeta,
        page: canteraPage,
        perPage: canteraPerPage,
        isEmpty: canteraIsEmpty,
        fetchDeportistas,
        detalleCache: canteraDetalleCache,
        detalleLoadingIds: canteraDetalleLoadingIds,
        detalleErrorCache: canteraDetalleErrorCache,
        loadDetalle: loadCanteraDetalle,
        isSelected: canteraIsSelected,
        selectedCount: canteraSelectedCount,
        totalSeleccionado: canteraTotalSeleccionado,
        togglePlazo: canteraTogglePlazo,
        pagoLoading: canteraPagoLoading,
        pagoError: canteraPagoError,
        pagarYRefrescarPlazos: canteraPagarYRefrescarPlazos,
        clearSelection: canteraClearSelection,
        listas: canteraListas,
        listasLoading: canteraListasLoading,
        selectedListaId: canteraSelectedListaId,
        onListaFilterChange: canteraOnListaFilterChange,
    } = useCantera();

    /**
     * Propaga el toggle de un plazo al composable.
     * Recibe { deportistaId, plazoId, estado }.
     */
    const onCanteraPlazoToggled = ({ deportistaId, plazoId, estado }) => {
        canteraTogglePlazo(deportistaId, plazoId, estado);
    };

    /**
     * El PaymentModal con supports3DS=true gestiona el pago internamente
     * (incluyendo 3DS). Solo necesitamos refrescar los detalles de los
     * deportistas cuyos plazos se acaban de pagar.
     */
    const onCanteraPagoCompletado = () => {
        // Averiguar qué deportistas tenían plazos seleccionados y cuántos plazos en total
        const affectedIds = [];
        let totalPlazosPagados = 0;
        for (const d of deportistas.value) {
            const detalle = canteraDetalleCache.value.get(d.id);
            if (!detalle?.plazos) continue;
            const selectedPlazos = detalle.plazos.filter(p => canteraIsSelected(d.id, p.id));
            if (selectedPlazos.length > 0) {
                affectedIds.push(d.id);
                totalPlazosPagados += selectedPlazos.length;
            }
        }

        canteraClearSelection();

        // Toast de confirmación
        const toast = useToast();
        const msg = totalPlazosPagados === 1
            ? t('perfil.plazo_pagado')
            : t('perfil.plazos_pagados');
        toast.success(msg);

        // Refrescar solo el detalle de los deportistas afectados
        for (const id of affectedIds) {
            canteraDetalleCache.value.delete(id);
            loadCanteraDetalle(id);
        }
    };
    // Read tab from query param on mount
    onMounted(() => {
        const tabParam = route.query.tab;
        if (tabParam && typeof tabParam === 'string') {
            changeTab(tabParam);
        }
    });

    const changeTab = tabId => {
        cancelEditing();
        changeTabBase(tabId);

        if (tabId === 'cantera') {
            fetchDeportistas(1);
        }

        if (tabId === 'pedidos') {
            fetchPedidos(1);
        }

        // Reset cambiar datos view when changing tabs
        if (tabId !== 'pedidos') {
            pedidoCambioDatos.value = null;
            pedidoEnviarEntradas.value = null;
            mostrandoInvitaciones.value = false;
        }
    };
</script>